import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, MessageCircle, Phone, Send } from "lucide-react";
import { BRAND } from "@/lib/site";
import { Label } from "@/components/Shared";

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD =
  "w-full rounded-xl border border-[#DCE5EF] bg-white px-4 py-3 text-[14px] text-[#10253A] outline-none transition-colors placeholder:text-[#9AA9B8] focus:border-[#0757B8]";

const GROUPS = [
  "Privatkunde",
  "Gewerbekunde",
  "Hausverwaltung",
  "Vermieter / Eigentümer",
  "Sonstiges",
];

const CONTRACTS = [
  "Saisonvertrag Winterdienst 2026/2027",
  "Laufender Winterdienstvertrag",
  "Einmalige Schneeräumung oder Streudienst",
  "Angebot für mehrere Objekte",
  "Individuelle Vereinbarung",
  "Ich bin unsicher und benötige Beratung",
];

const OBJECTS = [
  "Privathaus",
  "Mehrfamilienhaus / Wohnanlage",
  "Gewerbeobjekt",
  "Parkplatz / Zufahrt",
  "Sonstiges",
];

const SERVICES = [
  "Schneeräumung",
  "Streudienst",
  "Räumung von Gehwegen",
  "Räumung von Zufahrten und Parkflächen",
  "Eisbeseitigung",
  "Individuelle Anfrage",
];

const STARTS = ["Saison 2026/2027", "Möglichst zeitnah", "Individueller Termin"];

const BUSINESS_GROUPS = ["Gewerbekunde", "Hausverwaltung", "Vermieter / Eigentümer"];

const initial = {
  type: "Privatkunde",
  contract: CONTRACTS[0],
  name: "",
  company: "",
  phone: "",
  email: "",
  address: "",
  objectType: OBJECTS[0],
  size: "",
  services: ["Schneeräumung"],
  start: STARTS[0],
  note: "",
  privacy: false,
  website: "",
};

const emailOk = (v) => /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(v.trim());

const ContactForm = ({ defaultType }) => {
  const [f, setF] = useState({ ...initial, ...(defaultType ? { type: defaultType } : {}) });
  const [error, setError] = useState("");
  const [sent, setSent] = useState(null);
  const [loading, setLoading] = useState(false);

  const isBusiness = BUSINESS_GROUPS.includes(f.type);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const toggleService = (s) =>
    setF((p) => ({
      ...p,
      services: p.services.includes(s)
        ? p.services.filter((x) => x !== s)
        : [...p.services, s],
    }));

  const buildText = () =>
    [
      `Winterdienst-Anfrage (${f.type})`,
      `Vertragsart: ${f.contract}`,
      `Ansprechpartner: ${f.name}`,
      f.company && `Unternehmen / Verwaltung: ${f.company}`,
      `Telefon: ${f.phone}`,
      `E-Mail: ${f.email}`,
      `Objektadresse: ${f.address}`,
      `Art des Objekts: ${f.objectType}`,
      f.size && `Flächengröße: ${f.size}`,
      f.services.length && `Gewünschte Leistungen: ${f.services.join(", ")}`,
      `Gewünschter Beginn: ${f.start}`,
      f.note && `Nachricht: ${f.note}`,
    ]
      .filter(Boolean)
      .join("\n");

  const validate = () => {
    if (!f.name.trim()) return setError("Bitte Ihren Namen angeben."), false;
    if (!f.phone.trim()) return setError("Bitte eine Telefonnummer angeben."), false;
    if (!emailOk(f.email)) return setError("Bitte eine gültige E-Mail-Adresse angeben."), false;
    if (!f.address.trim())
      return setError("Bitte die Objektadresse mit Straße, PLZ und Ort angeben."), false;
    if (isBusiness && !f.company.trim())
      return setError("Bitte Unternehmen bzw. Hausverwaltung angeben."), false;
    if (!f.services.length) return setError("Bitte mindestens eine Leistung auswählen."), false;
    if (!f.privacy)
      return setError("Bitte bestätigen Sie die Datenschutzerklärung."), false;
    setError("");
    return true;
  };

  const sendWhatsApp = () => {
    if (!validate()) return;
    window.open(`${BRAND.whatsapp}?text=${encodeURIComponent(buildText())}`, "_blank", "noopener");
    setSent("WhatsApp");
  };

  const submit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/anfragen`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(f),
      });
      if (!res.ok) throw new Error("send failed");
      setSent("direkt");
    } catch {
      setError(
        "Die Anfrage konnte gerade nicht übermittelt werden. Bitte per WhatsApp oder telefonisch melden.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid="contact-form-success"
        className="flex flex-col justify-center rounded-[22px] border border-[#DCE5EF] bg-white p-8 text-center shadow-[0_40px_90px_-60px_rgba(6,43,82,0.6)] sm:p-12"
      >
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#EEF6FF]">
          <CheckCircle2 className="h-7 w-7 text-[#0757B8]" />
        </span>
        <h2 className="mt-6 text-[22px] font-bold leading-snug sm:text-[26px]">
          {sent === "direkt"
            ? "Anfrage ist eingegangen"
            : "Anfrage vorbereitet – jetzt noch absenden"}
        </h2>
        <p className="mx-auto mt-4 max-w-md text-[14.5px] leading-relaxed text-[#66788A]">
          {sent === "direkt"
            ? "Vielen Dank – Ihre unverbindliche Anfrage liegt uns vor. Wir prüfen Objekt und Flächen und melden uns persönlich mit einem Angebot zurück. Es ist dadurch noch kein Vertrag zustande gekommen."
            : "Ihre Angaben wurden in eine WhatsApp-Nachricht übernommen. Falls WhatsApp sich nicht automatisch geöffnet hat, nutzen Sie bitte einen der direkten Wege – wir melden uns nach Eingang Ihrer Anfrage persönlich zurück."}
        </p>
        <div className="mx-auto mt-8 grid w-full max-w-sm gap-2.5">
          <a href={BRAND.phoneHref} data-testid="success-phone" className="btn btn-dark h-13 py-3.5 text-[14px]">
            <Phone className="h-4 w-4" /> {BRAND.phone}
          </a>
          <a
            href={BRAND.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="success-whatsapp"
            className="btn btn-wa h-13 py-3.5 text-[14px]"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp öffnen
          </a>
          <button
            type="button"
            onClick={() => {
              setSent(null);
              setF(initial);
            }}
            data-testid="success-reset"
            className="btn btn-ghost h-13 py-3.5 text-[14px]"
          >
            Neue Anfrage erstellen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="contact-form"
      className="rounded-[22px] border border-[#DCE5EF] bg-white p-6 shadow-[0_40px_90px_-60px_rgba(6,43,82,0.6)] sm:p-8"
    >
      <Label>/ Angebot anfordern</Label>
      <h2 className="mt-3 text-[21px] font-bold leading-snug sm:text-[26px]">
        Winterdienst anfragen – Angebot für Ihr Objekt
      </h2>
      <p className="mt-3 text-[13.5px] leading-relaxed text-[#66788A]">
        Unverbindliche Anfrage – kein Vertragsabschluss. Kurz ausfüllen und absenden, die
        Anfrage geht direkt an unser Büro. Alternativ per WhatsApp. Pflichtfelder sind mit *
        gekennzeichnet.
      </p>

      <form className="mt-7 grid gap-5" onSubmit={(e) => e.preventDefault()}>
        {/* Honeypot */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={f.website}
          onChange={set("website")}
          className="absolute left-[-9999px] h-0 w-0 opacity-0"
        />

        <div>
          <span className="font-mono-label text-[#66788A]">Ich bin *</span>
          <div className="mt-2.5 flex flex-wrap gap-2" data-testid="form-group-row">
            {GROUPS.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setF((p) => ({ ...p, type: g }))}
                data-testid={`form-group-${g.split(" ")[0].toLowerCase().replace(/[^a-zäöü]/g, "")}`}
                className={`rounded-full border px-4 py-2.5 text-[13px] font-semibold transition-colors ${
                  f.type === g
                    ? "border-[#0757B8] bg-[#EEF6FF] text-[#0757B8]"
                    : "border-[#DCE5EF] bg-white text-[#66788A] hover:border-[#0757B8]"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="font-mono-label text-[#66788A]">Gewünschte Vertragsart *</span>
          <select
            className={`${FIELD} mt-2`}
            data-testid="form-contract"
            value={f.contract}
            onChange={set("contract")}
          >
            {CONTRACTS.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="font-mono-label text-[#66788A]">Name / Ansprechpartner *</span>
            <input
              className={`${FIELD} mt-2`}
              data-testid="form-name"
              value={f.name}
              onChange={set("name")}
              placeholder="Vor- und Nachname"
              autoComplete="name"
            />
          </label>
          <label className="block">
            <span className="font-mono-label text-[#66788A]">
              Unternehmen / Hausverwaltung {isBusiness ? "*" : "(optional)"}
            </span>
            <input
              className={`${FIELD} mt-2`}
              data-testid="form-company"
              value={f.company}
              onChange={set("company")}
              placeholder={isBusiness ? "Firmen- oder Verwaltungsname" : "Nur bei gewerblichen Anfragen"}
              autoComplete="organization"
            />
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="font-mono-label text-[#66788A]">Telefon *</span>
            <input
              className={`${FIELD} mt-2`}
              data-testid="form-phone"
              value={f.phone}
              onChange={set("phone")}
              placeholder="z. B. 0170 1234567"
              inputMode="tel"
              autoComplete="tel"
            />
          </label>
          <label className="block">
            <span className="font-mono-label text-[#66788A]">E-Mail *</span>
            <input
              className={`${FIELD} mt-2`}
              data-testid="form-email"
              value={f.email}
              onChange={set("email")}
              placeholder="name@beispiel.de"
              type="email"
              inputMode="email"
              autoComplete="email"
            />
          </label>
        </div>

        <label className="block">
          <span className="font-mono-label text-[#66788A]">
            Objektadresse – Straße, Hausnummer, PLZ, Ort *
          </span>
          <input
            className={`${FIELD} mt-2`}
            data-testid="form-address"
            value={f.address}
            onChange={set("address")}
            placeholder="z. B. Musterstraße 1, 28199 Bremen"
            autoComplete="street-address"
          />
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="font-mono-label text-[#66788A]">Art des Objekts</span>
            <select
              className={`${FIELD} mt-2`}
              data-testid="form-objecttype"
              value={f.objectType}
              onChange={set("objectType")}
            >
              {OBJECTS.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="font-mono-label text-[#66788A]">
              Ungefähre Flächengröße (optional)
            </span>
            <input
              className={`${FIELD} mt-2`}
              data-testid="form-size"
              value={f.size}
              onChange={set("size")}
              placeholder="z. B. 15 m Gehweg, 400 m² Hof"
            />
          </label>
        </div>

        <div>
          <span className="font-mono-label text-[#66788A]">Gewünschte Leistungen *</span>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {SERVICES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => toggleService(s)}
                data-testid={`form-service-${s.split(" ")[0].toLowerCase()}`}
                className={`rounded-full border px-3.5 py-2 text-[12.5px] font-medium transition-colors ${
                  f.services.includes(s)
                    ? "border-[#FF6B00] bg-[#FF6B00] text-white"
                    : "border-[#DCE5EF] bg-white text-[#10253A] hover:border-[#FF6B00]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="font-mono-label text-[#66788A]">Gewünschter Beginn</span>
          <select
            className={`${FIELD} mt-2`}
            data-testid="form-start"
            value={f.start}
            onChange={set("start")}
          >
            {STARTS.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="font-mono-label text-[#66788A]">
            Nachricht / zusätzliche Informationen
          </span>
          <textarea
            className={`${FIELD} mt-2 min-h-[96px] resize-y`}
            data-testid="form-note"
            value={f.note}
            onChange={set("note")}
            placeholder="Besonderheiten: Gefälle, Kopfsteinpflaster, Zeitfenster, Schlüssel/Zufahrt, Anzahl der Objekte …"
          />
        </label>

        <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-[#F5F8FC] p-4">
          <input
            type="checkbox"
            data-testid="form-privacy"
            checked={f.privacy}
            onChange={(e) => setF((p) => ({ ...p, privacy: e.target.checked }))}
            className="mt-0.5 h-4 w-4 shrink-0 accent-[#0757B8]"
          />
          <span className="text-[12.5px] leading-relaxed text-[#66788A]">
            Ich habe die{" "}
            <Link
              to="/datenschutz"
              data-testid="form-privacy-link"
              className="font-semibold text-[#0757B8] underline underline-offset-2"
            >
              Datenschutzerklärung
            </Link>{" "}
            gelesen und bin damit einverstanden, dass meine Angaben zur Bearbeitung der
            Anfrage gespeichert und verarbeitet werden. *
          </span>
        </label>

        {error && (
          <p
            data-testid="form-error"
            className="rounded-xl bg-[#FFF1F2] px-4 py-3 text-[13px] font-medium text-[#D81E2F]"
          >
            {error}
          </p>
        )}

        <div className="grid gap-2.5 sm:grid-cols-2">
          <button
            type="button"
            onClick={submit}
            disabled={loading}
            data-testid="form-submit"
            className="btn btn-orange h-13 py-3.5 text-[14px] disabled:opacity-70"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Wird gesendet …
              </>
            ) : (
              <>
                <Send className="h-4 w-4" /> Angebot anfordern
              </>
            )}
          </button>
          <button
            type="button"
            onClick={sendWhatsApp}
            data-testid="form-send-whatsapp"
            className="btn btn-wa h-13 py-3.5 text-[14px]"
          >
            <MessageCircle className="h-4 w-4" /> Lieber per WhatsApp
          </button>
        </div>
        <p className="flex items-start gap-2 text-[11.5px] leading-relaxed text-[#66788A]">
          <Send className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#0757B8]" />
          Mit „Angebot anfordern" senden Sie eine unverbindliche Anfrage an unser Büro – es
          entsteht kein Vertrag und keine Zahlungspflicht. Wir melden uns persönlich zurück.
        </p>
      </form>
    </div>
  );
};

export default ContactForm;
